import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export const SectionHeader = ({ index, eyebrow, title, align = "left", className = "" }) => (
  <div className={`${align === "center" ? "text-center" : "text-left"} ${className}`}>
    <p className="font-term text-[11px] tracking-[0.4em] text-electric" data-testid="section-index">
      {index} {"//"} {eyebrow}
    </p>
    <h2 className="mt-4 font-display text-3xl font-bold tracking-[0.08em] text-chrome sm:text-4xl lg:text-5xl">
      {title}
    </h2>
    <div
      className={`glow-line mt-6 w-32 ${align === "center" ? "mx-auto" : ""}`}
      aria-hidden="true"
    />
  </div>
);

export const GlowButton = ({ to, children, variant = "primary", testId, external = false }) => {
  const base =
    "clip-angular-sm group inline-flex items-center gap-3 px-7 py-4 font-term text-xs font-semibold tracking-[0.26em] transition-shadow duration-300";
  const styles =
    variant === "primary"
      ? "bg-arc text-white hover:shadow-[0_0_36px_rgba(22,119,255,0.75)]"
      : "border border-ice/30 text-ice hover:border-electric hover:shadow-[0_0_28px_rgba(0,168,255,0.35)]";
  const inner = (
    <>
      {children}
      <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </>
  );
  if (external) {
    return (
      <a href={to} target="_blank" rel="noopener noreferrer" data-testid={testId} className={`${base} ${styles}`}>
        {inner}
      </a>
    );
  }
  return (
    <Link to={to} data-testid={testId} className={`${base} ${styles}`}>
      {inner}
    </Link>
  );
};

export const StatusBadge = ({ status }) => {
  const map = {
    COMPLETED: "border-ice/40 text-ice",
    ACTIVE: "border-electric/60 text-electric shadow-[0_0_14px_rgba(0,168,255,0.35)]",
    QUEUED: "border-silver/40 text-silver",
    LOCKED: "border-steel text-silver/50",
    LIVE: "border-electric/60 text-electric",
    PLANNED: "border-silver/40 text-silver",
    VISION: "border-epurple/50 text-epurple",
    CONCEPT: "border-violetx/50 text-violetx",
    EXPLORATION: "border-ice/40 text-ice",
  };
  return (
    <span
      className={`inline-flex items-center gap-2 border px-3 py-1 font-term text-[10px] tracking-[0.3em] ${map[status] || "border-ice/30 text-ice"}`}
    >
      <span className="h-1 w-1 rounded-full bg-current" />
      {status}
    </span>
  );
};

export const HudRow = ({ label, value, accent = false }) => (
  <div className="flex items-center justify-between border-b border-ice/10 py-3">
    <span className="hud-label">{label}</span>
    <span className={`hud-value ${accent ? "text-electric text-glow-blue" : ""}`}>{value}</span>
  </div>
);

export const SimTag = ({ children = "SIMULATION" }) => (
  <span className="inline-flex items-center gap-2 border border-epurple/40 bg-epurple/10 px-3 py-1 font-term text-[10px] tracking-[0.3em] text-epurple">
    <span className="h-1 w-1 animate-blink rounded-full bg-epurple" />
    {children}
  </span>
);

export const Panel = ({ children, className = "", ...rest }) => (
  <div className={`glass-panel clip-angular relative ${className}`} {...rest}>{children}</div>
);
